import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, MotionConfig } from 'motion/react';
import { api } from './api';
import { applyLocale, bootRuntime, onVmProgress } from './engine/runtime';
import { BootScreen, FIRST_PROGRESS, type BootFailure, type BootProgress } from './components/BootScreen';
import { Ledger } from './components/home/Ledger';
import { Backup } from './components/home/Backup';
import { Invite } from './components/home/Invite';
import { Play } from './components/play/Play';
import { WorkspaceGate } from './components/play/WorkspaceGate';
import { Debrief } from './components/debrief/Debrief';
import { Opener } from './components/opener/Opener';
import { Profile } from './components/profile/Profile';
import { ChatDock } from './components/chat/ChatDock';
import { LangToggle } from './components/LangToggle';
import { ThemeToggle } from './components/ThemeToggle';
import { CornerFold } from './components/CornerFold';
import { MuteToggle } from './components/MuteToggle';
import { useLocale, useStrings } from './state/useLocale';
import { getStrings } from './lib/strings';
import { useAccount } from './state/useAccount';
import { useProgress } from './state/useProgress';
import { registerCourse, resumeBridge } from './lib/agent-bridge';
import { usePaid } from './state/usePaid';
import { useOffer } from './state/useOffer';
import { isBehind, levelProgress, type Progress } from './progress';
import { canPlay, gateFor, lockedMessage, paidSpan } from './lib/entitlement';
import { startCheckout, takeCheckoutHint, type Plan } from './lib/checkout';
import { redeemCode } from './lib/offer';
import { Toast, useToast } from './ui/toast';
import { bootFailed, bootReady, bootStarted, track, uncodedFailure } from './lib/events';
import { fetchOpenerIndex, type OpenerCard } from './lib/openers';
import { sound } from './lib/sound';
import type { PublicMission } from './types';
import { onward } from './lib/entitlement';


/* Both moved to ui/toast.tsx when the landing page became a second screen
   that mounts outside this component and still has a sign-in to report. The
   type is re-exported here because seven modules import it from this address
   and none of them has any other business with App. */
export type { ToastFn } from './ui/toast';

/** The five screens the query string can name, and null for the level list. */
interface Route {
  mission: string | null;
  /** A level's debrief: the article at `?debrief=<id>` (components/debrief). */
  debrief: string | null;
  /** A chapter's opener: the essay at `?opener=<slug>` (components/opener). */
  opener: string | null;
  /**
   * A level's workspace with the grading switched off: `?sandbox=<id>`.
   *
   * The same id as `mission`, and deliberately a separate parameter rather
   * than `?mission=x&mode=sandbox`: the two are different screens with
   * different rails and different rules, and a learner who lands on one
   * should be able to see which from the URL.
   */
  sandbox: string | null;
  /**
   * The learner's own record and the builder their operator is made in:
   * `?profile=me` (components/profile).
   *
   * A value rather than a bare flag, because a bare `?profile` is a parameter
   * whose value is the empty string and every reader of it then has to know
   * that "" and null are different things. `me` is what the product links to;
   * anything else in there is somebody else's idea and lands on the same
   * screen, which holds only this browser's record and cannot show another.
   *
   * Somebody else's record is a different screen at a different address —
   * `/u/<tag>`, rendered by main.tsx instead of this component, because it
   * must not boot a machine (components/profile/PublicProfile.tsx). This
   * router never sees that path.
   */
  profile: string | null;
}

function readRoute(search: string): Route {
  const params = new URLSearchParams(search);
  return {
    mission: params.get('mission'),
    debrief: params.get('debrief'),
    opener: params.get('opener'),
    sandbox: params.get('sandbox'),
    profile: params.get('profile'),
  };
}

/**
 * The whole router.
 *
 * The screen is still chosen by the query string, so every level is linkable
 * and the browser's own history works, but a level change must no longer
 * reload the page: the page now owns a running Linux, and reloading would
 * throw it away and spend another boot on the level next door.
 *
 * Every link in the product is a real `<a href="?mission=…">`, which is what
 * makes a level bookmarkable, middle-clickable and openable in a new tab.
 * One delegated listener turns the ordinary left click on one of them into a
 * `pushState`, and leaves every click a browser treats specially — a new tab,
 * a modified click, a download — to do what it has always done. Nothing else
 * in the tree has to know that navigation changed.
 *
 * The route is stored as the search string rather than as parsed fields, so
 * that adding a screen is a line in `readRoute` and nothing here. Storing the
 * string also keeps the identity stable: two reads of `?mission=l09` are the
 * same string and re-render nothing, where two freshly parsed objects would
 * be different every time.
 */
function useRoute(): Route {
  const [search, setSearch] = useState(() => window.location.search);

  useEffect(() => {
    const sync = () => setSearch(window.location.search);

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target.closest('a') : null;
      if (!target || target.target || target.hasAttribute('download')) return;
      // Only our own query-string links. A locked level's `href="#"`, an
      // absolute URL and an anchor are all somebody else's business.
      const href = target.getAttribute('href');
      if (!href || !href.startsWith('?')) return;

      const url = new URL(href, window.location.href);
      event.preventDefault();
      if (url.search === window.location.search) return;
      window.history.pushState(null, '', url.href);
      setSearch(url.search);
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', sync);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('popstate', sync);
      document.removeEventListener('click', onClick);
    };
  }, []);

  // Parsed on render rather than in state: `search` is the source of truth and
  // this is three string operations, which is cheaper than keeping two copies
  // of the same fact in sync.
  return useMemo(() => readRoute(search), [search]);
}

/**
 * A thrown value turned into something the boot screen can speak. The VM
 * package builds its errors with `Object.assign(new Error(msg), { code })`,
 * the way api.ts builds its status errors, so the code is a property and not
 * a class.
 */
function toFailure(err: unknown): BootFailure {
  const e = err as { code?: unknown; message?: unknown } | null;
  return {
    code: typeof e?.code === 'string' ? e.code : 'boot',
    detail: typeof e?.message === 'string' && e.message ? e.message : String(err),
  };
}

/**
 * What the tab must have before anything is downloaded: WebAssembly, and a
 * machine large enough to give a 256 MiB guest room beside the browser.
 * `deviceMemory` is Chromium-only and coarse by design; it is used only to
 * refuse the machines that certainly cannot, never to gate the ones that
 * merely might.
 */
function unsupportedReason(): BootFailure | null {
  if (typeof WebAssembly === 'undefined') {
    return { code: 'unsupported', detail: 'WebAssembly is not available in this browser.' };
  }
  const ram = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof ram === 'number' && ram > 0 && ram < 1) {
    return { code: 'memory', detail: `navigator.deviceMemory reports ${ram} GB.` };
  }
  return null;
}

/* There is no server to ask any more, and no other answer this can have: the
   workspace is the Linux in this tab. Kept as a label because the level list
   has always said where the learner's commands run, and that is worth saying
   more now than it was when it was a container on somebody's machine.

   It lives in the catalogue rather than here, and is read inside the component
   rather than at module scope: a constant captured at import time would keep
   saying it in whichever language the tab was opened in. */

export default function App() {
  const { locale } = useLocale();
  const s = useStrings();
  const [missions, setMissions] = useState<PublicMission[] | null>(null);
  /**
   * The chapter introductions, by world. A few kilobytes of titles and ledes
   * with no prose in them (web/src/lib/openers.ts), fetched once and shared by
   * the map and the clear overlay.
   *
   * Deliberately not part of the boot gate below: openers are decoration on a
   * page that works without them, and a learner must never wait on an essay
   * index to reach a level. It starts empty, which every reader of it treats
   * as "no openers", and fills in when it arrives.
   */
  const [openers, setOpeners] = useState<OpenerCard[]>([]);
  const [boot, setBoot] = useState<BootProgress>(FIRST_PROGRESS);
  const [bootError, setBootError] = useState<BootFailure | null>(null);
  const [bootAttempt, setBootAttempt] = useState(0);
  const { toast, notify, clear: clearToast } = useToast();

  // Who is signed in decides where progress is kept, so the account comes
  // first and the progress hook is handed the user.
  const account = useAccount(notify);
  const { progress, clear, attempt, skip, sync, restore } = useProgress(account.user);

  /**
   * The efficiency benchmark's tasks (docs/efficiency-bench.md), signed in
   * only: `?mission=bench-a1` opens one, from the study page or the agent's
   * open_level. Never in `missions`, which is the course and what the map
   * draws. Null while unknown, so a bench link does not read as a missing
   * level before they have arrived.
   */
  const [bench, setBench] = useState<PublicMission[] | null>(null);
  useEffect(() => {
    setBench(null);
    if (!account.user) {
      setBench([]);
      return undefined;
    }
    let live = true;
    void api.listBenchMissions().then(
      (list) => { if (live) setBench(list); },
      () => { if (live) setBench([]); },
    );
    return () => { live = false; };
  }, [account.user]);

  /**
   * Fold the paid half into the catalogue when this account owns it, and take
   * it back out when it does not.
   *
   * Called for its effect, not its answer. `unlockPaid` replaces the locked
   * stubs inside the `Map` the runtime already holds — and `relockPaid` puts
   * them back — which React has no way to notice, so the callback re-reads the
   * list that was built from it. The hook re-asks whenever the signed-in user
   * changes, which is what makes a purchase — or simply signing in on a second
   * device — arrive without a reload, and what makes signing out give the
   * levels back rather than leaving a tab holding a course it cannot play
   * (state/usePaid.ts).
   */
  const refreshMissions = useCallback(() => {
    void api.listMissions().then(
      (list) => setMissions(list),
      () => {
        /* the free half is already on screen; a failed re-read changes nothing */
      },
    );
  }, []);
  const paid = usePaid(account.user, refreshMissions);

  /**
   * What the course costs, and which ways there are to get it.
   *
   * Asked for once and not keyed on the account, unlike `paid` above: what
   * this learner owns changes when they sign in, and what the course costs
   * does not. It is fetched rather than bundled because the price lives in
   * the Worker's environment, so that changing it is a config deploy and not
   * a rebuild of this bundle (worker/stripe.ts). The currency travels with it
   * for the same reason and has already moved once.
   */
  const offer = useOffer();

  /**
   * Coming back from the payment provider.
   *
   * The hint in the URL decides only what is said, never what is owned: the
   * entitlement was settled by Stripe's signed webhook to the Worker, and
   * `refresh` goes and asks. A learner who types `?checkout=paid` themselves
   * gets a cheerful sentence and no levels, which is the correct amount of
   * both.
   *
   * There is nothing else in the URL to read, either. Stripe offers to
   * interpolate a `{CHECKOUT_SESSION_ID}` into the URL it returns the
   * customer to, and the Worker does not take it up — and `bounceHome`
   * rebuilds the address from the one parameter it recognises anyway
   * (worker/stripe.ts). So the tab is handed a word and never an identifier.
   * That is deliberate: a session id sitting in a query string is an
   * invitation to look it up, and a client that looked it up would be this
   * file deciding a payment question, which is the one thing the design says
   * only a signed webhook may do.
   */
  useEffect(() => {
    const hint = takeCheckoutHint();
    if (!hint) return;
    // `getStrings()`, not the `s` from render: this effect runs once, on the
    // load that came back from the payment provider, and reads the language
    // as it is at that moment rather than closing over one.
    if (hint === 'cancelled') {
      notify(getStrings().app.toast.checkoutCancelled);
      return;
    }
    notify(getStrings().app.toast.checkoutThanks);
    void (async () => {
      /*
       * What was just bought, named from the catalogue rather than written
       * into the sentence — and read *before* the unlock lands, because
       * `refresh` folds the paid levels into the catalogue in place and
       * afterwards there are no locked stubs left to read a span off.
       *
       * This used to sweep the smallest locked level to the largest level
       * anywhere and hand both to the sentence, with a `Number.isFinite`
       * guard turning the empty sweep into a zero. Two things were wrong with
       * it. The read happened *inside* the `then`, so the sweep it guarded
       * against was the ordinary case and not the edge one: a successful
       * purchase left nothing locked, `Math.min` of nothing is `Infinity`,
       * and the toast announced levels 0 to 51 — the whole course, half of
       * which the learner had been given free. And the sweep itself is the
       * claim `paidSpan` exists to stop making: it is one contiguous run
       * across the catalogue, which is a true description of the paid half
       * for exactly as long as there is one lane in it (lib/entitlement.ts,
       * docs/curriculum.md §6).
       *
       * So the span is asked for, and it is allowed to refuse. A range that
       * cannot be named honestly is not named at all — and the count is what
       * is said instead, because the count is the fact and the range is the
       * nicety (lib/entitlement.ts). A wholly paid lane has no legible range:
       * every track numbers itself from zero, so its `0-19` is a run of
       * numbers this learner has already played under another track's name.
       * "Twenty levels unlocked" is true of it and of every other shape the
       * paid half can take.
       *
       * Silence stood here while the count-shaped sentence was missing, which
       * was the honest stand-in and not the answer: somebody who has just
       * paid should be told what arrived. The invite dialog makes the same
       * choice between the same two sentences (components/home/Invite.tsx).
       *
       * A span of null is the one case that still says nothing more, and it
       * is not the same case: nothing was locked when the page loaded, so
       * either this account already owned the course or this build has no
       * paid half, and the thank-you above is the whole of what happened.
       */
      const span = paidSpan(await api.listMissions().catch(() => [] as PublicMission[]));
      await paid.refresh();
      const toast = getStrings().app.toast;
      if (span) {
        notify(span.range ? toast.checkoutUnlocked(span.range.from, span.range.to) : toast.checkoutUnlockedCount(span.count));
      }
    })();
    // Once, on the load that came back from the provider. `paid.refresh` is
    // stable and `notify` is a ref underneath, so neither re-runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnlock = useCallback((plan?: Plan) => {
    // Compared rather than passed through: a few buttons hand this straight to
    // `onClick`, and a click event is not a month.
    void startCheckout(plan === 'month' ? 'month' : 'quarter').then((result) => {
      // Recorded with its outcome, and the outcome is the point. Two of the
      // four are the product's fault rather than the learner's — `unavailable`
      // is a checkout nobody has switched on, `anonymous` is somebody who was
      // ready to pay and was asked to sign in first — and both are invisible
      // in a revenue figure, because both produce no revenue in exactly the
      // way that no interest also produces none (docs/analytics.md).
      track('checkout-start', { detail: result });
      // 'redirecting' never lands here in practice — the page is already on
      // its way to the provider. The rest are the reasons it did not start.
      if (result === 'anonymous') notify(s.app.toast.signInFirst);
      else if (result === 'unavailable') notify(s.app.toast.paymentsOff, 'error');
      // Not an error toast. Nothing went wrong and nothing was charged; the
      // learner pressed a button for something they already have.
      else if (result === 'owned') notify(s.app.toast.checkoutOwned);
      else if (result === 'failed') notify(s.app.toast.checkoutFailed, 'error');
    });
  }, [notify, s]);

  const route = useRoute();
  const missionId = route.mission;
  const [backupOpen, setBackupOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  // The backup dialog belongs to the level list: it is the screen where the
  // learner is looking at what they would lose. A Back or Forward that lands
  // on a level while it is open would otherwise leave it standing over the
  // workstation, which is not a place anyone asked to be interrupted.
  useEffect(() => {
    setBackupOpen(false);
  }, [missionId]);

  // The opener index, once per visit, off the critical path. It is a static
  // asset beside the page (not a Worker route — an opener holds no solution,
  // so it goes through no entitlement check), it cannot fail in a way that
  // matters, and nothing waits on it.
  useEffect(() => {
    void fetchOpenerIndex(locale).then(setOpeners);
  }, [locale]);

  /**
   * Refuse a file dropped on the page, everywhere the page does not want one.
   *
   * A browser's default for a file dropped on a document is to **navigate to
   * it**, and this is the one application where that is not a nuisance but a
   * loss. The workstation is the tab: the machine, its filesystem, the
   * terminal's scrollback, an unsaved editor buffer and a half-written chat
   * line all live in this page and none of them is on a server. A learner who
   * drags a screenshot a few pixels wide of the chat panel currently throws
   * all of it away and gets a PNG in a viewer, with no back button that
   * restores any of it.
   *
   * So the default is cancelled at the window, for both events — `dragover`
   * as well as `drop`, because a `drop` that was never allowed to happen does
   * not fire and cancelling only the second one does nothing at all.
   *
   * This does not stop anything from *accepting* a drop. A real target calls
   * `preventDefault` on its own way through the target phase and reads the
   * files there; by the time the event reaches this listener the work is
   * done, and cancelling an already-cancelled event costs nothing. What is
   * left is the default for everywhere else, which is the case this is about.
   *
   * On `window` rather than on the app's root element, because the margin
   * around the layout is part of the document too and a file dropped on it
   * navigates exactly as readily.
   */
  useEffect(() => {
    const refuse = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', refuse);
    window.addEventListener('drop', refuse);
    return () => {
      window.removeEventListener('dragover', refuse);
      window.removeEventListener('drop', refuse);
    };
  }, []);

  // Booting the VM and fetching the level list are independent, so they run
  // together and the boot screen stays until both are done. Nothing here
  // starts the VM that api.ts would not have started anyway: `bootRuntime` is
  // idempotent, and asking for it early is what lets the download begin while
  // the level list is still arriving.
  //
  // `bootRuntime` resolves once the guest answers *and* the warm-up phase has
  // run, because v86's JIT cache is not part of the saved state and the first
  // commands after a restore are 2–4× slower (docs/browser.md, Phase 0
  // results 5). That cost is paid behind this screen, never at the prompt.
  useEffect(() => {
    const unsupported = unsupportedReason();
    if (unsupported) {
      setBootError(unsupported);
      // Counted like any other boot failure, and it is the one the product
      // can least afford to be blind to: a browser refused here is a learner
      // who saw an apology and left, and nothing else in the system would
      // ever mention that it happened (docs/analytics.md).
      bootFailed(unsupported.code);
      return;
    }
    let live = true;
    // Annotated, not inferred: this is the one place the VM's `VmProgress`
    // meets the boot screen's copy of its shape, and the annotation is what
    // makes a divergence a compile error here rather than a silent adapter.
    const stopWatching = onVmProgress((p: BootProgress) => {
      if (live) setBoot(p);
    });
    void (async () => {
      // Marked here rather than inside the VM package, because what a learner
      // waits through is this whole effect — the catalogue and the machine —
      // and a duration measured from anywhere narrower would be a number that
      // flatters the product.
      bootStarted();
      try {
        const [list] = await Promise.all([api.listMissions(), bootRuntime()]);
        if (!live) return;
        if (list.length === 0) throw Object.assign(new Error('No levels are installed.'), { code: 'manifest' });
        // Re-read rather than publishing `list`, and this is load-bearing
        // rather than tidying.
        //
        // That snapshot was taken the moment the catalogue finished loading,
        // which is a fraction of a second in. The VM beside it takes several
        // orders of magnitude longer, and `unlockPaid` runs in that gap
        // whenever this account owns the paid half (state/usePaid.ts): it
        // replaces the locked stubs inside the same `Map` and has the shell
        // re-read the list, which it does correctly. Then this line lands,
        // seconds later, and posts the snapshot taken before any of that
        // happened -- putting the stubs back.
        //
        // What the learner saw was a paywall drawn over levels they had just
        // been given, on every reload, for as long as a boot takes. Redeeming
        // a code appeared to work because by then the boot was long finished
        // and there was nothing left to overwrite it.
        //
        // `listMissions` walks a Map that is already in memory, so re-reading
        // costs nothing, and it is correct whatever else has touched the
        // catalogue while the machine was starting.
        const fresh = await api.listMissions();
        if (!live) return;
        setMissions(fresh);
        bootReady();
      } catch (err) {
        const failure = toFailure(err);
        // Recorded whether or not this effect is still the live one. An
        // aborted boot that failed on the way out is still a boot that
        // failed, and the retry below will record its own outcome too.
        //
        // Not `failure.code`: that spells a throw with no code `boot`, so the
        // screen has a sentence to show, and the funnel then could not tell
        // our own bug from a guest that never answered. Here it is `script`,
        // or the narrower word its type allows (`uncodedFailure`).
        const code = (err as { code?: unknown } | null)?.code;
        bootFailed(typeof code === 'string' ? code : uncodedFailure(err));
        if (live) setBootError(failure);
      }
    })();
    return () => {
      live = false;
      stopWatching();
    };
  }, [bootAttempt]);

  const retryBoot = useCallback(() => {
    setBootError(null);
    setBoot(FIRST_PROGRESS);
    setBootAttempt((n) => n + 1);
  }, []);

  /**
   * Put the levels into the language the page is in.
   *
   * Once when the catalogue arrives, and again on every switch. The levels'
   * own words live in a file per language and are written onto the mission
   * objects in place (engine/runtime.ts, `applyLocale`) — so a switch costs
   * one small fetch and no reload, and the running Linux in this tab is not
   * disturbed by it.
   *
   * `refreshMissions` afterwards for the reason it is called after a
   * purchase: the mutation happens inside a `Map` that React cannot see, so
   * the list has to be re-read for anything to re-render. Without it the
   * interface would turn Arabic and the fifty level titles would not.
   *
   * Gated on `missions` rather than run eagerly, because `applyLocale` awaits
   * the catalogue and there is nothing to translate before it exists.
   */
  useEffect(() => {
    if (!missions) return;
    let live = true;
    void applyLocale(locale).then(() => {
      if (live) refreshMissions();
    });
    return () => {
      live = false;
    };
    // Deliberately not depending on `missions`: this must run when the
    // language changes, and when the catalogue first arrives — and
    // `refreshMissions` sets `missions`, so depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, missions === null, refreshMissions]);

  // The frontier, and the head of it: the hero CTA and the pulsing row have
  // one slot between them, and the rule for filling it is stated on `onward`.
  // A learner with two open levels — one skipped and come back to, one newly
  // reached — gets the earlier of them, which is what this has always done
  // and is the right way round: the map is showing them where they left off,
  // not where they could get to.
  const next = useMemo(
    () => (missions ? (onward(progress, missions, null, account.user !== null).open[0] ?? null) : null),
    [missions, progress, account.user],
  );

  // Landing on a level you cannot open yet falls back to the list. Say why,
  // otherwise the link looks broken. Explained once per level rather than
  // once per visit, now that a visit can reach several.
  const explained = useRef<string | null>(null);
  useEffect(() => {
    if (!missions || !missionId || explained.current === missionId) return;
    // A bench task is not a level and has no gate but the account's.
    if (missionId.startsWith('bench-')) {
      if (bench === null) return;
      if (!bench.some((m) => m.id === missionId)) {
        explained.current = missionId;
        notify(account.user ? s.app.toast.noSuchLevel : s.app.toast.benchSignIn);
      }
      return;
    }
    const target = missions.find((m) => m.id === missionId);
    if (!target) {
      explained.current = missionId;
      notify(s.app.toast.noSuchLevel);
    } else {
      const gate = gateFor(progress, target, missions, account.user !== null);
      if (gate.kind !== 'open') {
        explained.current = missionId;
        // The mission, not its level: the sentence has to name the
        // prerequisites this level actually asks for and quote only its own
        // lane's numbers, and a level number can no longer find either
        // (lib/entitlement.ts).
        notify(lockedMessage(gate, target, missions, account.user !== null));
      }
    }
  }, [missions, bench, missionId, progress, notify, account.user, s]);

  const handleBackup = useCallback(() => setBackupOpen(true), []);
  const closeBackup = useCallback(() => setBackupOpen(false), []);

  const openInvite = useCallback(() => setInviteOpen(true), []);
  const closeInvite = useCallback(() => setInviteOpen(false), []);

  /**
   * A code landed.
   *
   * The same call the checkout return makes, for the same reason: the dialog
   * has been told the Worker said yes, and that is not the same as this
   * browser holding thirty levels it did not have a moment ago. `refresh`
   * goes and fetches them, and the catalogue is re-read from what comes back.
   *
   * The dialog stays open and writes its own sentence. A toast would be wrong
   * here — the person is looking at the panel they just used, and the answer
   * belongs in it.
   */
  const handleRedeemed = useCallback(() => {
    void paid.refresh();
  }, [paid]);

  /**
   * A locked row was clicked, and the shell says why.
   *
   * The map hands back a mission id, so the mission is looked up to find out
   * *which* refusal this is. A paid level and an unreached one are both
   * "locked" to the row that was clicked, and they are not the same sentence.
   *
   * The id rather than the level number it used to be: `level` is a position
   * within a track and two tracks number themselves from zero, so a lookup by
   * number would find whichever lane came first in the catalogue and explain
   * the wrong level's refusal (docs/curriculum.md §5).
   *
   * **The not-found branch no longer invents a gate.** It used to fall back
   * to `'sequence'`, which produced "Clear Level N-1 first" for a mission the
   * catalogue does not have — a learner sent to go and clear a level, when
   * the truth is that the level they clicked is not there. The two are
   * opposite instructions: one says the course is waiting on them, the other
   * says the course lost a row. So the miss is reported as itself, in the
   * same words a `?mission=` URL naming an unknown id gets, because it is the
   * same fact arriving by a different door.
   *
   * An `open` gate is silence rather than a sentence. The row only calls this
   * when it refused a click, so the gate is open here only if the catalogue
   * changed between the render and the click — in which case the level is
   * playable and the honest response is to say nothing and let the second
   * click through.
   */
  const handleLocked = useCallback(
    (missionId: string) => {
      const target = missions?.find((m) => m.id === missionId);
      if (!missions || !target) {
        notify(s.app.toast.noSuchLevel);
        return;
      }
      const gate = gateFor(progress, target, missions, account.user !== null);
      if (gate.kind === 'open') return;
      notify(lockedMessage(gate, target, missions, account.user !== null));
    },
    [notify, missions, progress, account.user, s],
  );

  const mission = missions && missionId
    ? (missions.find((m) => m.id === missionId) ?? bench?.find((m) => m.id === missionId))
    : undefined;
  // The debrief screen. It needs no VM and no entitlement of its own: the
  // article is fetched from the Worker, which applies the same rule the paid
  // missions go through, and an unknown id simply falls through to the list.
  const debriefing = missions && route.debrief ? (missions.find((m) => m.id === route.debrief) ?? null) : null;
  // `canPlay`, not `isUnlocked`: this is the gate a typed URL, a bookmark and
  // a Back button all land on, and a paid level has to fall back to the map
  // here the same way an unreached one does.
  // A bench task is open to a signed-in account (the only kind that is sent one).
  const playable = missions && mission
    ? mission.track === 'bench' ? account.user !== null : canPlay(progress, mission, missions, account.user !== null)
    : false;

  // The sandbox is the same workspace with the grading switched off, so it
  // goes through the *same* gate. Nothing about "nothing is graded here"
  // makes it acceptable to hand someone a level they have not reached or not
  // bought: a sandbox that skipped `canPlay` would be a way to play the paid
  // half for free, minus a Check button nobody needs to pass a level they
  // only wanted to read.
  const scratch = missions && route.sandbox ? missions.find((m) => m.id === route.sandbox) : undefined;
  /**
   * The course, for the learner's own agent when they lent it this tab
   * (lib/agent-bridge.ts, docs/agents.md): the level list as the map shows
   * it, and opening a level the way a link on the map does. A level the map
   * would not open is refused here too.
   */
  const courseNow = useRef({ missions, bench, progress, signedIn: account.user !== null });
  courseNow.current = { missions, bench, progress, signedIn: account.user !== null };
  useEffect(() => registerCourse({
    levels: () => {
      const { missions: all, progress: p, signedIn } = courseNow.current;
      return (all ?? []).map((m) => ({
        id: m.id,
        title: m.title,
        track: m.track,
        level: m.level,
        cleared: levelProgress(p, m.id).completed,
        open: gateFor(p, m, all ?? [], signedIn).kind === 'open',
      }));
    },
    refuseOpen: (id) => {
      const { missions: all, bench: tasks, progress: p, signedIn } = courseNow.current;
      // A benchmark task (docs/efficiency-bench.md): by its id from the study page.
      if (id.startsWith('bench-')) {
        if (!signedIn) return 'Sign in first: a benchmark task is graded for this account.';
        if (tasks === null) return 'The benchmark tasks are still loading. Try again in a few seconds.';
        return tasks.some((x) => x.id === id) ? null : `No benchmark task has the id "${id}".`;
      }
      const m = all?.find((x) => x.id === id);
      if (!m) return `No level has the id "${id}". Use list_levels.`;
      return gateFor(p, m, all ?? [], signedIn).kind === 'open' ? null : 'That level is not open to this account yet: it needs an earlier level cleared, or the course.';
    },
    open: (id) => {
      const url = new URL(window.location.href);
      url.search = `?mission=${encodeURIComponent(id)}`;
      window.history.pushState(null, '', url.href);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
  }), []);
  // A tab lent before a reload is lent again, once there is somebody to lend it as.
  useEffect(() => {
    if (account.user) resumeBridge();
  }, [account.user]);

  const scratchGate = missions && scratch ? gateFor(progress, scratch, missions, account.user !== null) : null;
  const scratchable = scratchGate?.kind === 'open';

  // One tree, boot included, rather than an early return: the chat bubble at
  // the bottom of it is mounted once for the whole visit, and a return that
  // swapped the tree around it would close its socket the moment the VM
  // finished downloading — exactly when the wait made it worth having.
  return (
    <MotionConfig reducedMotion="user">
      {!missions || bootError ? (
        <BootScreen progress={boot} error={bootError} onRetry={retryBoot} />
      ) : route.profile !== null ? (
        // Ahead of every level screen and behind the boot, like the opener:
        // it needs no VM, no entitlement and no level, and it is the one
        // screen in the product that is about the person rather than about
        // the course. Inside the tree rather than an early return, so the
        // chat socket at the foot of it stays open while somebody changes
        // their face -- which is the moment it is worth watching.
        <Profile missions={missions} progress={progress} account={account} />
      ) : route.opener ? (
        // Ahead of the debrief, and well ahead of the level. An opener is the
        // one screen here that is not earned, not bought and not graded: no
        // entitlement, no cleared check, nothing but an essay and a door into
        // the chapter. It is also the screen a cold link from outside is most
        // likely to land on, so it answers first and asks nothing.
        <Opener key={route.opener} slug={route.opener} missions={missions} />
      ) : debriefing ? (
        // Before the level branch, so `?debrief=` wins if both are somehow in
        // the query string at once, and inside the tree rather than as an
        // early return, for the reason the boot screen is: ChatDock at the
        // bottom is mounted once for the visit and holds a live socket.
        <Debrief
          key={debriefing.id}
          mission={debriefing}
          missions={missions}
          progress={progress}
          cleared={levelProgress(progress, debriefing.id).completed}
          account={account}
          onUnlock={handleUnlock}
        />
      ) : route.sandbox ? (
        // Before the level branch so that `?sandbox=` wins if both are in the
        // query string, and keyed separately from the level: entering and
        // leaving a sandbox rebuilds the workspace, which is what makes it a
        // sandbox rather than a second view of the same session.
        scratchable && scratch ? <Play
          key={`sandbox:${scratch.id}`}
          mission={scratch}
          missions={missions}
          progress={progress}
          openers={openers}
          sandbox
          onClear={clear}
          onAttempt={attempt}
          onSkip={skip}
          notify={notify}
        /> : <WorkspaceGate mission={scratch} missions={missions}
          gate={scratchGate && scratchGate.kind !== 'open' ? scratchGate : null}
          signedIn={account.user !== null} busy={account.busy}
          onSignIn={account.signIn} onUnlock={handleUnlock} />
      ) : playable && mission ? (
        // Keyed on the level, because a level change is now a re-render rather
        // than a page load: the workstation's own state — the selected commit,
        // the clear overlay, whether this one has been celebrated — belongs to
        // the level and has to go with it.
        <Play
          key={mission.id}
          userId={account.user?.id ?? null}
          mission={mission}
          missions={missions}
          progress={progress}
          openers={openers}
          onClear={clear}
          onAttempt={attempt}
          onSkip={skip}
          notify={notify}
        />
      ) : (
        <Ledger
          missions={missions}
          progress={progress}
          next={next}
          openers={openers}
          driverLabel={s.app.driverLabel}
          account={account}
          sync={sync}
          onBackup={handleBackup}
          onLocked={handleLocked}
          onUnlock={handleUnlock}
          offer={offer}
          onInvite={openInvite}
        />
      )}
      {/* Mounted whatever the screen, and gated on `open` inside, the way Play
          mounts the warm-up: the dialog animates out, and a component that is
          torn off the tree cannot. */}
      <Backup open={backupOpen} progress={progress} restore={restore} onClose={closeBackup} />
      {/* Beside Backup and mounted on the same terms. A code is redeemed from
          the offer block at the foot of the level list, but the dialog lives
          here because what it changes — what this account owns — is the
          shell's state and not the list's. */}
      <Invite
        open={inviteOpen}
        missions={missions ?? []}
        account={account}
        redeem={redeemCode}
        onRedeemed={handleRedeemed}
        onClose={closeInvite}
      />
      <Toast toast={toast} onDone={clearToast} />
      <MuteToggle />
      {/* Beside the sound control and mounted for the whole visit, for the
          same reason: somebody who has landed in the wrong language cannot
          navigate a settings page written in it. */}
      <LangToggle />
      {/* And beside that. Dark is the default and the one everything here is
          designed against (styles/theme.css); this is the way out for the
          people who need one, and it has to be reachable from a level and
          from a chapter essay, not only from the first screen. */}
      <ThemeToggle />
      {/* And the way out of the four of them. Everything above is mounted for
          the whole visit because there is no settings page to file it under,
          which is the right answer for somebody who wants a control and the
          wrong one for somebody who is reading: a chapter essay runs to the
          foot of the window and the last two lines of it were underneath all
          this. One click folds the cluster into the edge, and this stays
          behind to bring it back (lib/corner.ts). */}
      <CornerFold />
      {/* Mounted once, not per screen: the socket, the unread count and where
          the panel was dragged to all belong to the visit rather than to what
          is behind them. The boot screen included — a learner waiting on a
          120MB download is the one most likely to want somebody to talk to. */}
      <ChatDock progress={progress} mission={playable && mission ? mission : null} notify={notify} account={account} />
    </MotionConfig>
  );
}
